import React from 'react';
import { CheckCircle, Shield, Sparkles } from 'lucide-react';

/**
 * EmptyStateCelebration - Displays when a metric card shows no issues
 * A subtle, celebratory visual to reward good data quality
 * 
 * Props:
 * @param {string} message - Short celebration message
 * @param {string} type - 'perfect' | 'good' | 'clean' (controls icon/color)
 * @param {boolean} isDarkMode - Theme flag
 * @param {boolean} compact - If true, renders inline (for inside metric cards)
 */
const EmptyStateCelebration = ({ message = 'All clear', type = 'clean', isDarkMode = false, compact = false }) => {
  
  const config = {
    perfect: {
      icon: Sparkles,
      gradient: isDarkMode 
        ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(6, 182, 212, 0.08) 100%)'
        : 'linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(6, 182, 212, 0.05) 100%)',
      color: isDarkMode ? '#34D399' : '#10B981',
      borderColor: isDarkMode ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.15)',
    },
    good: {
      icon: CheckCircle,
      gradient: isDarkMode 
        ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(139, 92, 246, 0.08) 100%)'
        : 'linear-gradient(135deg, rgba(59, 130, 246, 0.06) 0%, rgba(139, 92, 246, 0.04) 100%)',
      color: isDarkMode ? '#60A5FA' : '#3B82F6',
      borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.15)',
    },
    clean: {
      icon: Shield,
      gradient: isDarkMode 
        ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(52, 211, 153, 0.05) 100%)'
        : 'linear-gradient(135deg, rgba(16, 185, 129, 0.06) 0%, rgba(52, 211, 153, 0.04) 100%)',
      color: isDarkMode ? '#6EE7B7' : '#059669',
      borderColor: isDarkMode ? 'rgba(16, 185, 129, 0.15)' : 'rgba(16, 185, 129, 0.12)',
    }
  };

  const c = config[type] || config.clean;
  const Icon = c.icon;

  if (compact) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 10px',
        borderRadius: '8px',
        background: c.gradient,
        border: `1px solid ${c.borderColor}`,
        marginTop: '6px',
      }}>
        <Icon size={12} style={{ color: c.color }} />
        <span style={{ fontSize: '11px', color: c.color, fontWeight: '500' }}>
          {message}
        </span>
      </div>
    );
  }

  return (
    <div style={{
      padding: '32px',
      borderRadius: '12px',
      background: c.gradient,
      border: `1px solid ${c.borderColor}`,
      textAlign: 'center',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '12px',
    }}>
      <style>{`
        @keyframes celebratePulse {
          0%, 100% { transform: scale(1); opacity: 0.9; }
          50% { transform: scale(1.08); opacity: 1; }
        }
        @keyframes shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
      `}</style>
      <div style={{
        width: '48px',
        height: '48px',
        borderRadius: '50%',
        background: `${c.color}15`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        animation: 'celebratePulse 3s ease-in-out infinite',
      }}>
        <Icon size={24} style={{ color: c.color }} />
      </div>
      <div style={{
        fontSize: '16px',
        fontWeight: '600',
        color: c.color,
        background: isDarkMode
          ? `linear-gradient(90deg, ${c.color}, #fff, ${c.color})`
          : `linear-gradient(90deg, ${c.color}, ${c.color}dd, ${c.color})`,
        WebkitBackgroundClip: 'text',
        backgroundSize: '200% auto',
        animation: 'shimmer 3s linear infinite',
      }}>
        {message}
      </div>
    </div>
  );
};

export default EmptyStateCelebration;
